/**
 * ConfirmationStep — terminal wizard step shown after SAM calls finish_onboarding().
 *
 * phase="review"  → grouped sections with Edit affordances + voice hint + "Confirm" CTA.
 * phase="complete" → success screen; user clicks "Go to my workspace" to navigate.
 */
import React from 'react';
import { useGetTenantSettingsQuery } from '@/redux/internal-services/onboardingApi';

export interface ConfirmationStepProps {
  /** Controlled by parent (SetupWizardPage); switches UI from review to success. */
  phase: 'review' | 'complete';
  interactionMode: 'text' | 'voice';
  voiceGender?: 'male' | 'female' | null;
  /** Whether a voice session is actively running right now. Hides Edit buttons when true. */
  isVoiceActive?: boolean;
  /** Triggered by "Confirm and create workspace" or voice open_dashboard — does backend work. */
  onConfirm: () => void;
  /** Triggered by "Go to my workspace" — navigates to /chat. */
  onNavigate: () => void;
  finishing?: boolean;
  /** Lets the review phase navigate back to a specific wizard step. */
  onGoToStep?: (step: string) => void;
}

// ── Helpers ────────────────────────────────────────────────────────────────

function tzWithOffset(ianaName: string): string {
  try {
    const city = ianaName.split('/').pop()?.replace(/_/g, ' ') ?? ianaName;
    const fmt = new Intl.DateTimeFormat('en', { timeZone: ianaName, timeZoneName: 'shortOffset' });
    const offset = fmt.formatToParts(new Date()).find((p) => p.type === 'timeZoneName')?.value ?? '';
    return offset ? `${city} (${offset})` : city;
  } catch {
    return ianaName;
  }
}

// ── Sub-components ─────────────────────────────────────────────────────────

function Chip({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100">
      {label}
    </span>
  );
}

function DataRow({ label, value, swatch, chips }: { label?: string; value?: string; swatch?: string; chips?: string[] }) {
  if (!value && (!chips || chips.length === 0)) return null;
  return (
    <div className="py-2 flex items-start gap-2">
      {label ? <span className="text-xs text-gray-400 w-24 flex-shrink-0 pt-0.5">{label}</span> : null}
      {chips && chips.length > 0 ? (
        <div className={`flex flex-wrap gap-1 ${!label ? 'w-full' : ''}`}>
          {chips.map((c) => <Chip key={c} label={c} />)}
        </div>
      ) : (
        <span className="text-sm text-gray-800 font-medium flex items-center gap-1.5 min-w-0 break-words">
          {swatch && <span className="inline-block w-3 h-3 rounded-full border border-gray-200 flex-shrink-0" style={{ backgroundColor: swatch }} />}
          {value}
        </span>
      )}
    </div>
  );
}

function Section({
  title,
  onEdit,
  children,
}: {
  title: string;
  onEdit?: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-3 last:mb-0">
      <div className="flex items-center justify-between mb-1.5 px-1">
        <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">{title}</span>
        {onEdit && (
          <button
            onClick={onEdit}
            className="flex items-center gap-1 text-xs font-medium text-indigo-500 hover:text-indigo-700 transition"
          >
            <svg viewBox="0 0 14 14" fill="currentColor" className="w-3 h-3">
              <path d="M10.47 1.47a1.5 1.5 0 012.12 2.12L5.5 10.66l-2.5.5.5-2.5 6.97-7.19z" />
            </svg>
            Edit
          </button>
        )}
      </div>
      <div className="bg-white border border-gray-100 rounded-xl px-3 py-0.5 divide-y divide-gray-50">
        {children}
      </div>
    </div>
  );
}

// ── Review phase ───────────────────────────────────────────────────────────

function ReviewPhase({
  companyName, fullAddress, tzLabel, phone, territories, scopeOfWork,
  website, tagline, primaryColor, voiceText, isVoiceActive, finishing, onConfirm, onGoToStep,
}: {
  companyName: string; fullAddress: string; tzLabel: string; phone?: string;
  territories: string[]; scopeOfWork: string[];
  website?: string; tagline?: string; primaryColor?: string;
  voiceText: string; isVoiceActive: boolean;
  finishing: boolean; onConfirm: () => void; onGoToStep?: (step: string) => void;
}) {
  // When voice is actively running, user edits by speaking — hide Edit buttons to avoid breaking
  // the voice flow mid-session. SAM handles navigation via jump_to_step + finish_onboarding.
  // When voice is off, show Edit buttons so the user can tap to jump to any section.
  const canEdit = !isVoiceActive;
  const edit = (canEdit && onGoToStep) ? (step: string) => () => onGoToStep(step) : undefined;

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm">
      <h2 className="text-xl font-bold text-gray-800 mb-1">Review your company details</h2>
      <p className="text-gray-500 text-sm mb-4">Make sure everything looks right before we create your workspace.</p>

      <div className="text-left max-h-[46vh] overflow-y-auto no-scrollbar mb-5">
        <Section title="Company Info" onEdit={edit ? edit('company_info') : undefined}>
          <DataRow label="Name" value={companyName} />
          {fullAddress && <DataRow label="Address" value={fullAddress} />}
          {tzLabel && <DataRow label="Timezone" value={tzLabel} />}
          {phone && <DataRow label="Phone" value={phone} />}
        </Section>

        {territories.length > 0 && (
          <Section title="Service Areas" onEdit={edit ? edit('service_territory') : undefined}>
            <DataRow chips={territories} />
          </Section>
        )}

        {scopeOfWork.length > 0 && (
          <Section title="Work Types" onEdit={edit ? edit('scope_of_work') : undefined}>
            <DataRow chips={scopeOfWork} />
          </Section>
        )}

        {(website || tagline || primaryColor) && (
          <Section title="Branding" onEdit={edit ? edit('brand') : undefined}>
            {primaryColor && <DataRow label="Color" value={primaryColor} swatch={primaryColor} />}
            {website && <DataRow label="Website" value={website} />}
            {tagline && <DataRow label="Tagline" value={tagline} />}
          </Section>
        )}

        <Section title="Voice & Interaction" onEdit={edit ? edit('voice') : undefined}>
          <DataRow label="Mode" value={voiceText} />
        </Section>
      </div>

      <div className="space-y-2.5">
        <button
          onClick={onConfirm}
          disabled={finishing}
          className="w-full bg-indigo-600 text-white py-3 rounded-xl text-sm font-semibold hover:bg-indigo-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {finishing ? 'Creating your workspace…' : (
            <>
              Confirm and create workspace
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </>
          )}
        </button>
        {!canEdit && (
          <p className="text-center text-xs text-gray-400">
            Or say &ldquo;Everything looks good&rdquo; to confirm by voice.
          </p>
        )}
        {canEdit && onGoToStep && (
          <button
            onClick={() => onGoToStep('voice')}
            className="w-full text-gray-500 py-2 text-sm font-medium hover:text-gray-700 transition"
          >
            Back
          </button>
        )}
      </div>
    </div>
  );
}

// ── Complete phase ─────────────────────────────────────────────────────────

function CompletePhase({ companyName, onNavigate, finishing }: { companyName: string; onNavigate: () => void; finishing: boolean }) {
  return (
    <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
      <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-5">
        <svg viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8">
          <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
      </div>

      <h2 className="text-2xl font-bold text-gray-800 mb-2">Your workspace is ready!</h2>
      <p className="text-gray-500 text-sm mb-6">
        <strong>{companyName}</strong> has been successfully set up. You can now start using Smart Contractor to manage your business.
      </p>

      <div className="bg-gray-50 rounded-xl p-4 mb-4 text-left">
        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">What you can do next</p>
        <div className="space-y-3">
          {[
            'Use the Smart Contractor Assistant through voice or chat to create properties, capture leads, schedule work, and manage your daily activities.',
            'Open Settings to update company details, invite team members, manage users, and configure your workspace.',
            'Visit the Dashboard to view your work, tasks, appointments, and recent activity.',
          ].map((item, i) => (
            <div key={i} className="flex gap-2.5">
              <div className="w-5 h-5 rounded-full bg-indigo-100 flex-shrink-0 flex items-center justify-center mt-0.5">
                <span className="text-indigo-600 text-xs font-bold">{i + 1}</span>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">{item}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-indigo-50 rounded-xl px-4 py-3 mb-6 text-sm text-indigo-700 text-left">
        You can simply start by saying:{' '}
        <span className="font-semibold italic">&ldquo;Help me create my first lead.&rdquo;</span>
      </div>

      <button
        onClick={onNavigate}
        disabled={finishing}
        className="w-full bg-indigo-600 text-white py-3 rounded-xl text-sm font-semibold hover:bg-indigo-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {finishing ? 'Setting up…' : (
          <>
            Go to my workspace
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </>
        )}
      </button>
    </div>
  );
}

// ── Root ───────────────────────────────────────────────────────────────────

const ConfirmationStep: React.FC<ConfirmationStepProps> = ({
  phase,
  interactionMode,
  voiceGender,
  isVoiceActive = false,
  onConfirm,
  onNavigate,
  finishing = false,
  onGoToStep,
}) => {
  const { data: tenantSettings } = useGetTenantSettingsQuery();
  const tenant = tenantSettings?.data as Record<string, unknown> | undefined;

  const companyName = (tenant?.company_name as string) || (tenant?.name as string) || 'Your company';

  const address = tenant?.address as Record<string, string> | undefined;
  const addrParts = [
    address?.street?.trim(),
    [address?.city, address?.state, address?.zip].filter(Boolean).join(', '),
    address?.country?.trim(),
  ].filter(Boolean);
  const fullAddress = addrParts.join(' · ');

  const timezone = (tenant?.timezone as string | undefined)?.trim();
  const tzLabel = timezone ? tzWithOffset(timezone) : '';
  const phone = (tenant?.phone as string | undefined)?.trim();

  const branding = tenant?.branding as Record<string, string> | undefined;
  const website = branding?.website?.trim();
  const tagline = branding?.tagline?.trim();
  const primaryColor = branding?.primary_color?.trim();

  const territories = (tenant?.service_territories as string[] | undefined) ?? [];
  const scopeOfWork = (tenant?.scope_of_work as string[] | undefined) ?? [];

  const voiceText =
    interactionMode === 'voice'
      ? `Voice${voiceGender ? ` — ${voiceGender.charAt(0).toUpperCase() + voiceGender.slice(1)}` : ''}`
      : 'Text only';

  if (phase === 'complete') {
    return <CompletePhase companyName={companyName} onNavigate={onNavigate} finishing={finishing} />;
  }

  return (
    <ReviewPhase
      companyName={companyName}
      fullAddress={fullAddress}
      tzLabel={tzLabel}
      phone={phone}
      territories={territories}
      scopeOfWork={scopeOfWork}
      website={website}
      tagline={tagline}
      primaryColor={primaryColor}
      voiceText={voiceText}
      isVoiceActive={isVoiceActive}
      finishing={finishing}
      onConfirm={onConfirm}
      onGoToStep={onGoToStep}
    />
  );
};

export default ConfirmationStep;
